import React, { useState, useEffect } from "react";
import Input from "../common/Input";
import TextArea from "../common/TextArea";
import { API_ENDPOINTS, getAuthHeaders, shouldIncludeCredentials } from '../../lib/config/api-endpoints';

interface AppointmentType {
  id: number;
  name: string;
  description: string;
  duration_minutes: number;
  price_cents: number;
  currency: string;
  appointment_category: "entry" | "follow-up" | "specialty" | "package";
  prerequisites: string; // JSON array
  stripe_product_id?: string;
  stripe_price_id?: string;
  active: boolean;
  sort_order: number;
  max_bookings_per_client?: number;
  metadata: string; // JSON object
  created_at: string;
  updated_at: string;
}

interface AppointmentFormData {
  name: string;
  description: string;
  duration_minutes: number;
  price_dollars: number; // We'll convert to cents
  appointment_category: "entry" | "follow-up" | "specialty" | "package";
  prerequisites: number[]; // Array of appointment type IDs
  sort_order: number;
  max_bookings_per_client: number | null;
  active: boolean;
}

export const AdminAppointmentManager: React.FC = () => {
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<AppointmentType | null>(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState<AppointmentFormData>({
    name: "",
    description: "",
    duration_minutes: 60,
    price_dollars: 0,
    appointment_category: "follow-up",
    prerequisites: [],
    sort_order: 0,
    max_bookings_per_client: null,
    active: true,
  });

  useEffect(() => {
    fetchAppointmentTypes();
  }, []);

  const fetchAppointmentTypes = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.business.appointmentTypes, {
        headers: getAuthHeaders(API_ENDPOINTS.business.appointmentTypes),
        credentials: shouldIncludeCredentials(API_ENDPOINTS.business.appointmentTypes) ? 'include' : 'same-origin',
      });

      if (!response.ok) {
        throw new Error("Failed to fetch appointment types");
      }

      const data = await response.json();
      setAppointmentTypes(data.success ? data.data.appointment_types : []);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load appointment types"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof AppointmentFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePrerequisiteChange = (
    appointmentTypeId: number,
    checked: boolean
  ) => {
    setFormData((prev) => ({
      ...prev,
      prerequisites: checked
        ? [...prev.prerequisites, appointmentTypeId]
        : prev.prerequisites.filter((id) => id !== appointmentTypeId),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError("Appointment name is required");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Prepare data for API
      const apiData = {
        name: formData.name,
        description: formData.description,
        duration_minutes: formData.duration_minutes,
        price_cents: Math.round(formData.price_dollars * 100), // Convert to cents
        appointment_category: formData.appointment_category,
        prerequisites: JSON.stringify(formData.prerequisites),
        sort_order: formData.sort_order,
        max_bookings_per_client: formData.max_bookings_per_client,
        active: formData.active,
      };

      const url = editingItem
        ? `${API_ENDPOINTS.business.appointmentTypes}/${editingItem.id}`
        : API_ENDPOINTS.business.appointmentTypes;

      const method = editingItem ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(url),
        credentials: shouldIncludeCredentials(url) ? 'include' : 'same-origin',
        body: JSON.stringify(apiData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error ||
            `Failed to ${editingItem ? "update" : "create"} appointment type`
        );
      }

      // Reset form and refresh list
      resetForm();
      fetchAppointmentTypes();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Operation failed");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item: AppointmentType) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description,
      duration_minutes: item.duration_minutes,
      price_dollars: item.price_cents / 100, // Convert from cents
      appointment_category: item.appointment_category,
      prerequisites: JSON.parse(item.prerequisites || "[]"),
      sort_order: item.sort_order,
      max_bookings_per_client: item.max_bookings_per_client ?? null,
      active: item.active,
    });
    setShowForm(true);
  };

  const handleDelete = async (item: AppointmentType) => {
    if (
      !confirm(
        `Are you sure you want to delete "${item.name}"? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.business.appointmentTypes}/${item.id}`, {
        method: "DELETE",
        headers: getAuthHeaders(`${API_ENDPOINTS.business.appointmentTypes}/${item.id}`),
        credentials: shouldIncludeCredentials(API_ENDPOINTS.business.appointmentTypes) ? 'include' : 'same-origin',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete appointment type");
      }

      fetchAppointmentTypes();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      duration_minutes: 60,
      price_dollars: 0,
      appointment_category: "follow-up",
      prerequisites: [],
      sort_order: 0,
      max_bookings_per_client: null,
      active: true,
    });
    setEditingItem(null);
    setShowForm(false);
  };

  const formatPrice = (priceInCents: number) => {
    return `$${(priceInCents / 100).toFixed(2)}`;
  };

  const getCategoryBadge = (category: string) => {
    const colors = {
      entry: "bg-blue-100 text-blue-800",
      "follow-up": "bg-green-100 text-green-800",
      specialty: "bg-purple-100 text-purple-800",
      package: "bg-orange-100 text-orange-800",
    };
    return (
      colors[category as keyof typeof colors] || "bg-gray-100 text-gray-800"
    );
  };

  // Available appointment types for prerequisites (exclude current item if editing)
  const availableForPrerequisites = appointmentTypes.filter((apt) =>
    editingItem ? apt.id !== editingItem.id : true
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        Loading appointment types...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">
          Appointment Types Management
        </h2>
        <button
          onClick={() => {
            resetForm();
            setShowForm(!showForm);
          }}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
        >
          {showForm ? "Cancel" : "Create New Appointment Type"}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {showForm && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingItem
              ? "Edit Appointment Type"
              : "Create New Appointment Type"}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Appointment Name"
                id="appointment-name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="e.g., Follow-Up Session (30 minutes)"
                required
              />

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Category
                </label>
                <select
                  value={formData.appointment_category}
                  onChange={(e) =>
                    handleInputChange("appointment_category", e.target.value)
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="entry">Entry (First appointment)</option>
                  <option value="follow-up">Follow-Up</option>
                  <option value="specialty">Specialty</option>
                  <option value="package">Package</option>
                </select>
              </div>
            </div>

            <TextArea
              label="Description"
              id="appointment-description"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              rows={3}
              placeholder="Describe what this appointment includes..."
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Duration (minutes)"
                id="duration"
                type="number"
                value={formData.duration_minutes.toString()}
                onChange={(e) =>
                  handleInputChange(
                    "duration_minutes",
                    parseInt(e.target.value) || 60
                  )
                }
                required
                placeholder="15"
              />

              <Input
                label="Price (USD)"
                id="price"
                type="number"
                value={formData.price_dollars.toString()}
                onChange={(e) =>
                  handleInputChange(
                    "price_dollars",
                    parseFloat(e.target.value) || 0
                  )
                }
                placeholder="0.00"
                required
              />

              <Input
                label="Sort Order"
                id="sort-order"
                type="number"
                value={formData.sort_order.toString()}
                onChange={(e) =>
                  handleInputChange("sort_order", parseInt(e.target.value) || 0)
                }
                placeholder="0"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Max Bookings Per Client (blank = unlimited)"
                id="max-bookings"
                type="number"
                value={formData.max_bookings_per_client?.toString() || ""}
                onChange={(e) =>
                  handleInputChange(
                    "max_bookings_per_client",
                    e.target.value ? parseInt(e.target.value) : null
                  )
                }
                placeholder="1"
              />

              <div className="flex items-center space-x-2 pt-8">
                <input
                  type="checkbox"
                  id="is-active"
                  checked={formData.active}
                  onChange={(e) =>
                    handleInputChange("active", e.target.checked)
                  }
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="is-active"
                  className="text-sm font-medium text-gray-700"
                >
                  Active (available for booking)
                </label>
              </div>
            </div>

            {availableForPrerequisites.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prerequisites (appointments that must be completed first)
                </label>
                <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded p-3">
                  {availableForPrerequisites.map((apt) => (
                    <div key={apt.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`prereq-${apt.id}`}
                        checked={formData.prerequisites.includes(apt.id)}
                        onChange={(e) =>
                          handlePrerequisiteChange(apt.id, e.target.checked)
                        }
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor={`prereq-${apt.id}`}
                        className="text-sm text-gray-700"
                      >
                        {apt.name} ({apt.appointment_category})
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={saving}
                className="bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg transition-colors"
              >
                {saving
                  ? "Saving..."
                  : editingItem
                    ? "Update Appointment Type"
                    : "Create Appointment Type"}
              </button>

              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">
            Existing Appointment Types ({appointmentTypes.length})
          </h3>
        </div>

        {appointmentTypes.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No appointment types created yet. Create your first appointment type
            to get started.
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {appointmentTypes
              .sort((a, b) => a.sort_order - b.sort_order)
              .map((item) => (
                <div key={item.id} className="p-6 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="text-lg font-medium text-gray-900">
                          {item.name}
                        </h4>
                        <span
                          className={`px-2 py-1 text-xs rounded-full ${getCategoryBadge(item.appointment_category)}`}
                        >
                          {item.appointment_category}
                        </span>
                        <span
                          className={`px-2 py-1 text-xs rounded-full ${
                            item.active
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {item.active ? "Active" : "Inactive"}
                        </span>
                        {item.stripe_product_id && (
                          <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                            Stripe Synced
                          </span>
                        )}
                      </div>

                      <p className="text-gray-600 mb-3">{item.description}</p>

                      <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                        <span>Duration: {item.duration_minutes} minutes</span>
                        <span>Price: {formatPrice(item.price_cents)}</span>
                        <span>Order: {item.sort_order}</span>
                        {item.max_bookings_per_client && (
                          <span>
                            Max per client: {item.max_bookings_per_client}
                          </span>
                        )}
                        <span>
                          Created:{" "}
                          {new Date(item.created_at).toLocaleDateString()}
                        </span>
                      </div>

                      {item.prerequisites &&
                        JSON.parse(item.prerequisites).length > 0 && (
                          <div className="mt-2">
                            <span className="text-sm font-medium text-gray-700">
                              Prerequisites:{" "}
                            </span>
                            <span className="text-sm text-gray-500">
                              {JSON.parse(item.prerequisites)
                                .map((prereqId: number) => {
                                  const prereq = appointmentTypes.find(
                                    (apt) => apt.id === prereqId
                                  );
                                  return prereq?.name || `ID ${prereqId}`;
                                })
                                .join(", ")}
                            </span>
                          </div>
                        )}
                    </div>

                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => handleEdit(item)}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors"
                      >
                        Edit
                      </button>

                      <button
                        onClick={() => handleDelete(item)}
                        className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};
